'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const studioCapabilities = [
    {
        title: 'Program Management',
        desc: 'Build and organize educational programs with structured modules, content, and learning objectives.',
        span: 'lg:col-span-2',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="2" y="2" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <rect x="12" y="2" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <rect x="2" y="12" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <rect x="12" y="12" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.75" />
            </svg>
        ),
    },
    {
        title: 'Live Classroom',
        desc: 'Conduct interactive live sessions with built-in tools for engagement, Q&A, and attendance.',
        span: '',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="2" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M9 9l5 2.5L9 14V9z" fill="currentColor" />
            </svg>
        ),
    },
    {
        title: 'Assessment Builder',
        desc: 'Create tests, quizzes, and assignments with automated grading and detailed result analytics.',
        span: '',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="3" y="2" width="16" height="19" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M7 8h8M7 12h5M7 16h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M14 16l1.5 1.5L18 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Schedule & Availability',
        desc: 'Set your teaching hours, manage bookings, and let students schedule sessions without the back-and-forth.',
        span: '',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="2" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M16 1v4M6 1v4M2 9h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="11" cy="15" r="3" stroke="currentColor" strokeWidth="1.5" />
                <path d="M11 13.5v1.5l1 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Analytics & Insights',
        desc: 'Track student performance, engagement metrics, and institutional KPIs from a unified dashboard.',
        span: 'lg:col-span-2',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M3 16l5-6 4 4 5-8 4 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="1" y="1" width="20" height="20" rx="3" stroke="currentColor" strokeWidth="1.5" />
            </svg>
        ),
    },
    {
        title: 'Billing & Payments',
        desc: 'Manage tuition, session fees, and institutional billing with transparent financial reporting.',
        span: '',
        icon: (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="2" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M2 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M6 14h3M14 14h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        ),
    },
];

export default function WislyStudioSection() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.1 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            id="wisly-studio"
            ref={sectionRef}
            className="py-28 px-6 md:px-10 relative overflow-hidden"
            style={{ background: '#F5F6FF' }}
        >
            {/* Background accent */}
            <div
                className="absolute bottom-0 left-0 w-[600px] h-[600px] pointer-events-none"
                style={{ background: 'radial-gradient(circle at 20% 80%, rgba(55,87,255,0.08) 0%, transparent 70%)' }}
            />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="grid lg:grid-cols-2 gap-16 items-end mb-20">
                    <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                            style={{ background: 'rgba(55,87,255,0.1)', color: BRAND, border: '1px solid rgba(55,87,255,0.2)', letterSpacing: '0.15em' }}
                        >
                            Wisly Studio
                        </div>
                        <h2
                            className="font-display font-800 mb-5"
                            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 1.05, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Everything you need
                            <br />
                            <span style={{ color: BRAND }}>to teach and manage.</span>
                        </h2>
                        <p className="text-lg leading-relaxed" style={{ color: '#555' }}>
                            Wisly Studio is the operational workspace for tutors and educational institutions. It provides the tools needed to teach, manage, organize, and grow an educational operation.
                        </p>
                    </div>

                    <div className={`transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <p className="text-base leading-relaxed mb-8" style={{ color: '#666' }}>
                            From schedules and classrooms to students, assessments, staff, analytics, billing, and content — Studio gives educators and institutions a single, powerful workspace.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="/studio"
                                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-700 text-white transition-all duration-300"
                                style={{ background: BRAND, boxShadow: '0 8px 24px rgba(55,87,255,0.25)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                            >
                                Learn More
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M2.5 7h9M7.5 3.5l3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Capabilities bento */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {studioCapabilities.map((cap, i) => (
                        <div
                            key={cap.title}
                            className={`${cap.span} rounded-2xl p-7 group transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                            style={{
                                background: 'white',
                                border: '1px solid rgba(55,87,255,0.1)',
                                transitionDelay: `${i * 70}ms`,
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLElement).style.borderColor = BRAND;
                                (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(55,87,255,0.1)';
                                (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.1)';
                                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                            }}
                        >
                            <div
                                className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-all duration-300"
                                style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}
                            >
                                {cap.icon}
                            </div>
                            <h4 className="text-base font-700 mb-2" style={{ color: '#0D0D0D', letterSpacing: '-0.01em' }}>{cap.title}</h4>
                            <p className="text-sm leading-relaxed" style={{ color: '#666' }}>{cap.desc}</p>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA banner */}
                <div
                    className={`mt-12 rounded-3xl p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                    style={{ background: BRAND, boxShadow: '0 24px 64px rgba(55,87,255,0.3)' }}
                >
                    <div>
                        <p className="text-xs font-700 uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.15em' }}>Get started today</p>
                        <h3 className="font-display font-800 text-white mb-2" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.025em' }}>
                            Ready to connect your
                            <br />
                            educational ecosystem?
                        </h3>
                        <p style={{ color: 'rgba(255,255,255,0.7)' }} className="text-base">
                            Join the platform built for how education actually works.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                        <a
                            href="/early-access"
                            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-sm font-700 transition-all duration-300"
                            style={{ background: 'white', color: BRAND }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                        >
                            Request early access
                        </a>
                        <a
                            href="/contact"
                            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-sm font-600 transition-all duration-300"
                            style={{ color: 'white', border: '1.5px solid rgba(255,255,255,0.3)' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.1)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                            Schedule a demo
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
